/**
 * use-cloud-sync.ts
 *
 * Real cloud sync hook for Write Library.
 * Replaces the previous stub that only logged to console.
 *
 * Strategy:
 *   - On mount: pull from cloud, merge with local (cloud wins on version conflict)
 *   - On novel change: debounce 2s, then push to cloud
 *   - On blur/visibilitychange: immediate push
 *   - Conflict UI: surfaces to parent via onConflict callback
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../lib/store';

const API_URL = import.meta.env.VITE_API_URL || 'https://antigravity-api.gutlessbook.workers.dev';
const DEBOUNCE_MS = 2000;

export interface SyncConflict {
    novelId: string;
    novelTitle: string;
    serverVersion: number;
    localVersion: number;
    serverUpdatedAt: string;
    /** Call this to accept the server version */
    acceptServer: () => void;
    /** Call this to keep local version (force-pushes local to server) */
    keepLocal: () => Promise<void>;
}

interface UseCloudSyncOptions {
    /** Called when a sync conflict is detected that needs user resolution */
    onConflict?: (conflict: SyncConflict) => void;
    /** Called when sync status changes */
    onSyncStatusChange?: (status: 'idle' | 'syncing' | 'error' | 'offline') => void;
}

export function useCloudSync({ onConflict, onSyncStatusChange }: UseCloudSyncOptions = {}) {
    const novels = useAppStore(s => s.novels);
    const isSyncing = useAppStore(s => s.isSyncing);
    const loadNovelsFromCloud = useAppStore(s => s.loadNovelsFromCloud);
    const updateNovel = useAppStore(s => s.updateNovel);
    const importNovel = useAppStore(s => s.importNovel);

    // Track which novel IDs have pending local changes
    const pendingSync = useRef<Set<string>>(new Set());
    const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const lastSyncTime = useRef<string | null>(null);
    const isMounted = useRef(true);

    // ─── Helpers ────────────────────────────────────────────────────────────────

    const notifyStatus = useCallback((status: 'idle' | 'syncing' | 'error' | 'offline') => {
        onSyncStatusChange?.(status);
    }, [onSyncStatusChange]);

    /**
     * Push a single novel to the cloud.
     * Returns true on success.
     */
    const pushNovel = useCallback(async (novelId: string): Promise<boolean> => {
        const novel = useAppStore.getState().novels.find(n => n.id === novelId);
        if (!novel) return false;

        notifyStatus('syncing');

        try {
            const response = await fetch(`${API_URL}/api/novels`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ novel }),
            });

            if (response.status === 401) {
                // Not logged in — silently skip, local data is safe
                notifyStatus('idle');
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            // Update the cloudSyncVersion in local state so we know what's on the server
            if (data.version) {
                updateNovel(novelId, { cloudSyncVersion: data.version });
            }

            pendingSync.current.delete(novelId);
            notifyStatus('idle');
            return true;
        } catch (err) {
            console.error(`[CloudSync] Failed to push novel ${novelId}:`, err);
            notifyStatus('error');
            return false;
        }
    }, [updateNovel, notifyStatus]);

    /**
     * Pull all novels from cloud and merge into local state.
     * Called on mount and after reconnect.
     */
    const pullFromCloud = useCallback(async () => {
        if (!isMounted.current) return;
        await loadNovelsFromCloud();
        lastSyncTime.current = new Date().toISOString();
    }, [loadNovelsFromCloud]);

    /**
     * Run a sync check — compare local versions against server.
     * Detects conflicts and surfaces them via onConflict callback.
     */
    const runSyncCheck = useCallback(async () => {
        const currentNovels = useAppStore.getState().novels;
        if (currentNovels.length === 0) return;

        notifyStatus('syncing');

        try {
            const localVersions = currentNovels.map(n => ({
                id: n.id,
                version: n.cloudSyncVersion ?? 0,
            }));

            const response = await fetch(`${API_URL}/api/novels/sync`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lastSyncTime: lastSyncTime.current,
                    localNovels: localVersions,
                }),
            });

            if (response.status === 401) {
                notifyStatus('idle');
                return;
            }

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const { novels: serverNovels, conflicts, serverTime } = await response.json();

            // Apply non-conflicting server updates
            for (const serverNovel of serverNovels ?? []) {
                const local = currentNovels.find(n => n.id === serverNovel.id);
                if (!local) {
                    // New novel from another device — import it
                    importNovel(serverNovel);
                } else if ((serverNovel.cloudSyncVersion ?? 0) > (local.cloudSyncVersion ?? 0)) {
                    // Server is newer — apply it
                    importNovel(serverNovel);
                }
            }

            // Surface conflicts to UI
            for (const conflict of conflicts ?? []) {
                const local = currentNovels.find(n => n.id === conflict.id);
                if (!local || !onConflict) continue;

                onConflict({
                    novelId: conflict.id,
                    novelTitle: local.title,
                    serverVersion: conflict.serverVersion,
                    localVersion: conflict.localVersion,
                    serverUpdatedAt: conflict.serverUpdatedAt,
                    acceptServer: () => {
                        importNovel(conflict.novel);
                    },
                    keepLocal: async () => {
                        await pushNovel(conflict.id);
                    },
                });
            }

            lastSyncTime.current = serverTime;
            notifyStatus('idle');
        } catch (err) {
            console.error('[CloudSync] Sync check failed:', err);
            notifyStatus('error');
        }
    }, [importNovel, onConflict, pushNovel, notifyStatus]);

    // ─── Schedule debounced push for a novel ────────────────────────────────────

    const scheduleSync = useCallback((novelId: string) => {
        pendingSync.current.add(novelId);

        // Clear existing timer for this novel
        const existing = debounceTimers.current.get(novelId);
        if (existing) clearTimeout(existing);

        const timer = setTimeout(() => {
            if (isMounted.current) {
                pushNovel(novelId);
            }
            debounceTimers.current.delete(novelId);
        }, DEBOUNCE_MS);

        debounceTimers.current.set(novelId, timer);
    }, [pushNovel]);

    // ─── Watch novels for changes ────────────────────────────────────────────────

    const prevNovelsRef = useRef<typeof novels>([]);

    useEffect(() => {
        const prev = prevNovelsRef.current;

        for (const novel of novels) {
            const prevNovel = prev.find(n => n.id === novel.id);

            if (!prevNovel) {
                // New novel — sync immediately
                pushNovel(novel.id);
                continue;
            }

            // Check if content has changed by comparing updatedAt
            if (novel.updatedAt !== prevNovel.updatedAt) {
                scheduleSync(novel.id);
            }
        }

        prevNovelsRef.current = novels;
    }, [novels, scheduleSync, pushNovel]);

    // ─── On mount: pull from cloud ────────────────────────────────────────────

    useEffect(() => {
        isMounted.current = true;
        pullFromCloud();

        return () => {
            isMounted.current = false;
        };
    }, [pullFromCloud]);

    // ─── Flush pending syncs on blur / tab hide ──────────────────────────────

    useEffect(() => {
        const flushPending = () => {
            if (pendingSync.current.size === 0) return;

            // Clear all debounce timers
            for (const [novelId, timer] of debounceTimers.current) {
                clearTimeout(timer);
                pushNovel(novelId);
            }
            debounceTimers.current.clear();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flushPending();
            } else {
                // Tab became visible again — run a sync check
                runSyncCheck();
            }
        };

        const handleBeforeUnload = () => {
            flushPending();
        };

        window.addEventListener('blur', flushPending);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('blur', flushPending);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [pushNovel, runSyncCheck]);

    // ─── Online/offline detection ────────────────────────────────────────────

    useEffect(() => {
        const handleOnline = () => {
            notifyStatus('syncing');
            for (const novelId of pendingSync.current) {
                pushNovel(novelId);
            }
            runSyncCheck();
        };

        const handleOffline = () => {
            notifyStatus('offline');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [pushNovel, runSyncCheck, notifyStatus]);

    return {
        isSyncing,
        pendingCount: pendingSync.current.size,
        /** Manually trigger a full sync check */
        syncNow: runSyncCheck,
        /** Manually push a specific novel */
        pushNovel,
    };
}
